import type { UserId } from "@inrev/common";
import {
	useAdminFetchAgencyPreviews,
	useAdminFetchAgentPreviewsForAgency,
} from "@portal/api/agency/admin";
import { useAdminSubmitBondRequest } from "@portal/api/bond-request/admin";
import { useRequireActiveAgentFollower } from "@portal/api/followers";
import { useFetchAccountPreviews } from "@portal/api/previews";
import { createFormResolver } from "@portal/components/multiform/utils/formResolver";
import { useBondRequestFlowAnalytics } from "@portal/hooks/useBondRequestFlowAnalytics";
import { FormSchemaProvider } from "@portal/providers/FormSchemaProvider";
import { useCallback, useEffect, useMemo, useState } from "react";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import { useSearchParams } from "react-router-dom";
import { QuickSubmitHeader } from "../../../account/create/quick-submit/QuickSubmitHeader";
import {
	filterPrincipalPreviewsByAgency,
	getPrincipalPreviewsMapFromAccountPreviewsWithAgency,
} from "../../../shared/submission/getPrincipalPreviewsMapWithAgency";
import {
	AdminBondRequestContext,
	type AdminBondRequestSelectedAccount,
} from "../AdminBondRequestContext";
import { AdminBondRequestCreateModal } from "../AdminBondRequestCreateModal";
import { SelectedAdminAccountLoader } from "../SelectedAdminAccountLoader";
import {
	type AdminBondRequestFormData,
	adminBondRequestFormSchema,
	getDefaultAdminBondRequestFormData,
} from "../schema";
import { BondRequestQuickSubmit } from "./BondRequestQuickSubmit";

export const BondRequestQuickSubmitController = ({ onClose }: { onClose: () => void }) => {
	const { submitBondRequest, isSubmitting } = useAdminSubmitBondRequest();
	const [isFullSubmissionOpen, setIsFullSubmissionOpen] = useState(false);
	const [searchParams, setSearchParams] = useSearchParams();
	const [previousAgentId, setPreviousAgentId] = useState<string>("");

	const formMethods = useForm<AdminBondRequestFormData>({
		mode: "onBlur",
		resolver: createFormResolver(adminBondRequestFormSchema),
		defaultValues: getDefaultAdminBondRequestFormData(),
	});

	const { accountPreviews } = useFetchAccountPreviews();
	const { agencyPreviews } = useAdminFetchAgencyPreviews();

	const principalPreviewsMap = useMemo(
		() => getPrincipalPreviewsMapFromAccountPreviewsWithAgency(accountPreviews || []),
		[accountPreviews],
	);

	const selectedAgencyId = useWatch({ control: formMethods.control, name: "agencyId" });
	const selectedAgentId = useWatch({ control: formMethods.control, name: "agentId" });
	const followers = useWatch({ control: formMethods.control, name: "followers" });

	const filteredPrincipalPreviewsMap = useMemo(
		() => filterPrincipalPreviewsByAgency(principalPreviewsMap, selectedAgencyId),
		[selectedAgencyId, principalPreviewsMap],
	);

	const { agentPreviews: agencyAgents } = useAdminFetchAgentPreviewsForAgency(
		selectedAgencyId,
		true,
	);
	const agentPreviews = agencyAgents?.filter((agent) => !agent.invitationPending);

	const [selectedAccount, setSelectedAccount] = useState<AdminBondRequestSelectedAccount | null>(
		null,
	);

	// Principal → Account side effect
	const principalCompanyId = useWatch({
		control: formMethods.control,
		name: "quote.principal.id",
	});

	const principalCompany = useMemo(() => {
		if (!principalCompanyId) return undefined;
		return principalPreviewsMap[principalCompanyId];
	}, [principalCompanyId, principalPreviewsMap]);

	const accountId = principalCompany?.accountId;
	const analytics = useBondRequestFlowAnalytics("quick");
	const { onEntered, onCreated, onFailed } = analytics;

	useEffect(() => {
		if (!accountId) return;
		onEntered(accountId);
	}, [accountId, onEntered]);

	useEffect(() => {
		if (!principalCompanyId) {
			setSelectedAccount(null);
			return;
		}

		if (principalCompany) {
			formMethods.setValue("quote.principal.name", principalCompany.name);
		}
	}, [principalCompanyId, principalCompany, formMethods]);

	// Agency → Underwriter side effect
	const defaultUnderwriterId = useMemo(() => {
		if (selectedAgencyId && agencyPreviews) {
			return agencyPreviews.find((agency) => agency.id === selectedAgencyId)?.underwriterUserId;
		}
	}, [selectedAgencyId, agencyPreviews]);

	useEffect(() => {
		if (defaultUnderwriterId) {
			formMethods.setValue("underwriterId", defaultUnderwriterId);
			if (formMethods.formState.errors.underwriterId) {
				formMethods.trigger("underwriterId");
			}
		}
	}, [defaultUnderwriterId, formMethods]);

	// Agent → Followers side effect
	useEffect(() => {
		if (!selectedAgentId) return;
		if (selectedAgentId === previousAgentId) return;

		const currentFollowers = formMethods.getValues("followers") || [];
		let updatedFollowers = [...currentFollowers];

		if (previousAgentId) {
			updatedFollowers = updatedFollowers.filter((followerId) => followerId !== previousAgentId);
		}

		if (!updatedFollowers.includes(selectedAgentId)) {
			updatedFollowers.push(selectedAgentId);
		}
		formMethods.setValue("followers", updatedFollowers);

		if (formMethods.formState.errors.followers || formMethods.formState.errors.agentId) {
			formMethods.trigger(["followers", "agentId"]);
		}

		setPreviousAgentId(selectedAgentId);
	}, [selectedAgentId, previousAgentId, formMethods]);

	useRequireActiveAgentFollower(followers, agencyAgents, {
		setError: (message) => formMethods.setError("followers", { type: "manual", message }),
		clearError: () => formMethods.clearErrors("followers"),
		hasManualError: formMethods.formState.errors.followers?.type === "manual",
	});

	const onFollow = useCallback(
		(id: UserId) => {
			formMethods.setValue("followers", [...(followers || []), id]);
			if (formMethods.formState.errors.followers) {
				formMethods.trigger("followers");
			}
		},
		[followers, formMethods],
	);

	const onUnfollow = useCallback(
		(id: UserId) => {
			formMethods.setValue(
				"followers",
				(followers || []).filter((follower) => follower !== id),
			);
		},
		[followers, formMethods],
	);

	const handleSubmit = useCallback(() => {
		formMethods.handleSubmit((data) => {
			submitBondRequest({
				data,
				accountId: data.suretyAccountId,
				onSuccess: (bondRequest) => {
					onCreated(data.suretyAccountId, bondRequest.id);
					onClose();
				},
				onError: (error) =>
					onFailed(data.suretyAccountId, error instanceof Error ? error.name : "Unknown error"),
			});
		})();
	}, [onClose, formMethods, submitBondRequest, onCreated, onFailed]);

	const { markEscalatedToFullForm } = analytics;
	const handleOpenFullSubmission = useCallback(() => {
		markEscalatedToFullForm();
		setIsFullSubmissionOpen(true);
	}, [markEscalatedToFullForm]);

	const handleFullSubmissionClose = useCallback(() => {
		onClose();
		if (searchParams.get("section")) {
			setSearchParams("", { replace: true });
		}
	}, [onClose, searchParams, setSearchParams]);

	useEffect(() => {
		return () => {
			setSearchParams(
				(prev) => {
					const next = new URLSearchParams(prev);
					next.delete("section");
					next.delete("st");
					return next;
				},
				{ replace: true },
			);
		};
	}, []);

	const header = (
		<QuickSubmitHeader
			title="New Bond Request"
			agentPreviews={agencyAgents}
			onFollow={onFollow}
			onUnfollow={onUnfollow}
		/>
	);

	if (isFullSubmissionOpen) {
		return (
			<AdminBondRequestCreateModal
				onClose={handleFullSubmissionClose}
				externalFormMethods={formMethods}
				analytics={analytics}
			/>
		);
	}

	return (
		<AdminBondRequestContext.Provider
			value={{
				principalPreviewsMap,
				filteredPrincipalPreviewsMap,
				selectedAccount,
				setSelectedAccount,
			}}
		>
			<FormProvider {...formMethods}>
				{accountId && <SelectedAdminAccountLoader accountId={accountId} />}
				<FormSchemaProvider value={adminBondRequestFormSchema}>
					<BondRequestQuickSubmit
						onSubmit={handleSubmit}
						openFullSubmission={handleOpenFullSubmission}
						header={header}
						agentPreviews={agentPreviews}
						isSubmitting={isSubmitting}
					/>
				</FormSchemaProvider>
			</FormProvider>
		</AdminBondRequestContext.Provider>
	);
};
